import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiArrowUp,
  FiArrowDown,
  FiCalendar,
  FiCheck,
  FiX,
  FiStar,
  FiMail,
  FiPhone,
  FiUser,
  FiMessageSquare,
  FiMapPin,
  FiClock,
  FiCalendar as FiCalendarIcon,
  FiHome,
  FiInfo,
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiMoreVertical,
} from "react-icons/fi";
import {
  MdTimeline,
  MdAccessTime,
  MdLocationOn,
  MdEventAvailable,
  MdVerifiedUser,
} from "react-icons/md";

const EnquiriesManagement = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [viewingEnquiry, setViewingEnquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showExportOptions, setShowExportOptions] = useState(false);

  const [formData, setFormData] = useState({
    project_id: "",
    name: "",
    email: "",
    phone: "",
    note: "",
    preferred_time: "",
    ready_to_buy_timeline: "",
    site_visit_required: "",
  });

  const API_BASE = "https://dev.mithahomes.com/backend/api ";
  const ENQUIRIES_API = `https://dev.mithahomes.com/backend/api/enquired/enquiries`;
  const PROJECTS_API = `https://dev.mithahomes.com/backend/api/mainprojectprocess/maincard
`;

  // Timeline options
  const timelineOptions = [
    "Immediate",
    "Within 1 Month",
    "1-3 Months",
    "3-6 Months",
    "6-12 Months",
    "Just Exploring",
  ];

  // Preferred time options
  const timeOptions = [
    "Morning",
    "Afternoon",
    "Evening",
    "Anytime",
    "Weekdays",
    "Weekends",
  ];

  // Site visit options
  const siteVisitOptions = ["Yes", "No"];

  // Fetch all enquiries
  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(ENQUIRIES_API);
      if (response.data.success) {
        setEnquiries(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      toast.error("❌ Error loading enquiries");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all projects
  const fetchProjects = async () => {
    try {
      const response = await axios.get(PROJECTS_API);
      if (response.data.success) {
        setProjects(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("❌ Error loading projects");
    }
  };

  useEffect(() => {
    fetchEnquiries();
    fetchProjects();
  }, []);

  // Get project name by ID
  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.pid == projectId);
    return project ? project.project_name : "Unknown Project";
  };

  // Filter enquiries
  const filteredEnquiries = enquiries.filter((enquiry) => {
    const matchesSearch =
      enquiry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.phone?.includes(searchTerm) ||
      enquiry.note?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.id?.toString().includes(searchTerm);

    const matchesProject =
      filterProject === "all" || enquiry.project_id == filterProject;

    // Status filter logic (based on timeline)
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "hot" &&
        enquiry.ready_to_buy_timeline === "Immediate") ||
      (filterStatus === "warm" &&
        ["Within 1 Month", "1-3 Months"].includes(
          enquiry.ready_to_buy_timeline,
        )) ||
      (filterStatus === "cold" && !enquiry.ready_to_buy_timeline) ||
      ["3-6 Months", "6-12 Months", "Just Exploring"].includes(
        enquiry.ready_to_buy_timeline,
      );

    return matchesSearch && matchesProject && matchesStatus;
  });

  // Sort enquiries
  const sortedEnquiries = React.useMemo(() => {
    if (!sortConfig.key) return filteredEnquiries;

    return [...filteredEnquiries].sort((a, b) => {
      // Handle date sorting
      if (sortConfig.key === "created_at") {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      // Handle numeric sorting
      if (sortConfig.key === "id") {
        return sortConfig.direction === "asc" ? a.id - b.id : b.id - a.id;
      }

      // Handle null values
      if (!a[sortConfig.key] && b[sortConfig.key]) return -1;
      if (a[sortConfig.key] && !b[sortConfig.key]) return 1;
      if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;

      // Handle string sorting
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredEnquiries, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedEnquiries.length / itemsPerPage);
  const paginatedEnquiries = sortedEnquiries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      project_id: "",
      name: "",
      email: "",
      phone: "",
      note: "",
      preferred_time: "",
      ready_to_buy_timeline: "",
      site_visit_required: "",
    });
    setEditingEnquiry(null);
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("❌ Please enter name");
      return false;
    }
    if (!formData.email.trim()) {
      toast.error("❌ Please enter email");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("❌ Please enter a valid email address");
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error("❌ Please enter phone number");
      return false;
    }
    if (!formData.project_id) {
      toast.error("❌ Please select a project");
      return false;
    }
    return true;
  };

  // Submit enquiry
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setProcessing(true);

      let response;
      if (editingEnquiry) {
        response = await axios.put(
          `${ENQUIRIES_API}/${editingEnquiry.id}`,
          formData,
        );
      } else {
        response = await axios.post(ENQUIRIES_API, formData);
      }

      if (response.data.success) {
        toast.success(
          `✅ ${editingEnquiry ? "Enquiry updated" : "Enquiry added"} successfully!`,
        );
        setShowModal(false);
        resetForm();
        fetchEnquiries();
      } else {
        throw new Error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving enquiry:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving enquiry";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setProcessing(false);
    }
  };

  // Edit enquiry
  const handleEdit = (enquiry) => {
    setEditingEnquiry(enquiry);
    setFormData({
      project_id: enquiry.project_id || "",
      name: enquiry.name || "",
      email: enquiry.email || "",
      phone: enquiry.phone || "",
      note: enquiry.note || "",
      preferred_time: enquiry.preferred_time || "",
      ready_to_buy_timeline: enquiry.ready_to_buy_timeline || "",
      site_visit_required: enquiry.site_visit_required || "",
    });
    setShowModal(true);
  };

  // View enquiry details
  const handleView = (enquiry) => {
    setViewingEnquiry(enquiry);
    setShowViewModal(true);
  };

  // Delete enquiry
  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this enquiry? This action cannot be undone.",
      )
    ) {
      try {
        const response = await axios.delete(`${ENQUIRIES_API}/${id}`);

        if (response.data.success) {
          toast.success("🗑️ Enquiry deleted successfully!");
          fetchEnquiries();
        } else {
          throw new Error(response.data.message || "Delete failed");
        }
      } catch (error) {
        console.error("Error deleting enquiry:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error deleting enquiry";
        toast.error(`❌ ${errorMessage}`);
      }
    }
  };

  // Bulk delete
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select enquiries to delete");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedRows.length} selected enquiries?`,
      )
    ) {
      selectedRows.forEach(async (id) => {
        try {
          await axios.delete(`${ENQUIRIES_API}/${id}`);
        } catch (error) {
          console.error(`Error deleting enquiry ${id}:`, error);
        }
      });
      toast.success(
        `🗑️ ${selectedRows.length} enquiries deleted successfully!`,
      );
      setSelectedRows([]);
      fetchEnquiries();
    }
  };

  // Select/deselect all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(paginatedEnquiries.map((enquiry) => enquiry.id));
    } else {
      setSelectedRows([]);
    }
  };

  // Handle row selection
  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get timeline color
  const getTimelineColor = (timeline) => {
    switch (timeline) {
      case "Immediate":
        return "#ef4444";
      case "Within 1 Month":
        return "#f97316";
      case "1-3 Months":
        return "#eab308";
      case "3-6 Months":
        return "#22c55e";
      case "6-12 Months":
        return "#3b82f6";
      case "Just Exploring":
        return "#8b5cf6";
      default:
        return "#6b7280";
    }
  };

  // Get status badge
  const getStatusBadge = (enquiry) => {
    const timeline = enquiry.ready_to_buy_timeline;

    if (!timeline) {
      return {
        text: "Not Specified",
        color: "#6b7280",
        bgColor: "#f3f4f6",
      };
    }

    if (timeline === "Immediate") {
      return {
        text: "Hot Lead",
        color: "#dc2626",
        bgColor: "#fee2e2",
      };
    } else if (["Within 1 Month", "1-3 Months"].includes(timeline)) {
      return {
        text: "Warm Lead",
        color: "#d97706",
        bgColor: "#fef3c7",
      };
    } else {
      return {
        text: "Cold Lead",
        color: "#2563eb",
        bgColor: "#dbeafe",
      };
    }
  };

  // Get site visit badge
  const getSiteVisitBadge = (value) => {
    if (value === "Yes") {
      return {
        text: "Site Visit",
        color: "#059669",
        bgColor: "#d1fae5",
        icon: <FiCheck />,
      };
    } else if (value === "No") {
      return {
        text: "No Visit",
        color: "#dc2626",
        bgColor: "#fee2e2",
        icon: <FiX />,
      };
    }
    return {
      text: "Not Specified",
      color: "#6b7280",
      bgColor: "#f3f4f6",
      icon: <FiInfo />,
    };
  };

  // Export data
  const exportToCSV = () => {
    const headers = [
      "ID",
      "Name",
      "Email",
      "Phone",
      "Project",
      "Note",
      "Preferred Time",
      "Timeline",
      "Site Visit",
      "Date",
    ];
    const csvData = [
      headers.join(","),
      ...filteredEnquiries.map((enquiry) =>
        [
          enquiry.id,
          `"${enquiry.name}"`,
          enquiry.email,
          enquiry.phone,
          `"${getProjectName(enquiry.project_id)}"`,
          `"${enquiry.note || ""}"`,
          enquiry.preferred_time || "",
          enquiry.ready_to_buy_timeline || "",
          enquiry.site_visit_required || "",
          formatDateTime(enquiry.created_at),
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `enquiries-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success("📥 CSV exported successfully!");
    setShowExportOptions(false);
  };

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "30px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "800",
              color: "#1e293b",
              margin: "0 0 8px 0",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Enquiries Management
          </h1>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#64748b",
              margin: "0",
            }}
          >
            {enquiries.length} enquiries • {projects.length} projects
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          {selectedRows.length > 0 && (
            <button
              onClick={handleBulkDelete}
              style={{
                background: "#ef4444",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "10px",
                fontSize: "0.95rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0 2px 10px rgba(239, 68, 68, 0.3)",
              }}
            >
              <FiTrash2 /> Delete Selected ({selectedRows.length})
            </button>
          )}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowExportOptions(!showExportOptions)}
              style={{
                background: "white",
                color: "#475569",
                border: "2px solid #e2e8f0",
                padding: "10px 20px",
                borderRadius: "10px",
                fontSize: "0.95rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FiDownload /> Export
            </button>
            {showExportOptions && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  background: "white",
                  borderRadius: "10px",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                  padding: "10px",
                  marginTop: "5px",
                  zIndex: 100,
                  minWidth: "200px",
                }}
              >
                <button
                  onClick={exportToCSV}
                  style={{
                    width: "100%",
                    padding: "10px",
                    textAlign: "left",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.95rem",
                    color: "#475569",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  📊 Export to CSV
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowModal(true)}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
              transition: "all 0.3s ease",
            }}
          >
            <FiPlus /> Add New Enquiry
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {[
          {
            title: "Total Enquiries",
            value: enquiries.length,
            icon: <FiMessageSquare />,
            color: "#667eea",
            trend: "+12%",
          },
          {
            title: "Hot Leads",
            value: enquiries.filter(
              (e) => e.ready_to_buy_timeline === "Immediate",
            ).length,
            icon: <FiStar />,
            color: "#ef4444",
            trend: "+8%",
          },
          {
            title: "Site Visits",
            value: enquiries.filter((e) => e.site_visit_required === "Yes")
              .length,
            icon: <FiHome />,
            color: "#10b981",
            trend: "+15%",
          },
          {
            title: "Today",
            value: enquiries.filter((e) => {
              const today = new Date().toDateString();
              return new Date(e.created_at).toDateString() === today;
            }).length,
            icon: <FiCalendar />,
            color: "#f59e0b",
            trend: "+5%",
          },
        ].map((stat, index) => (
          <div
            key={index}
            style={{
              background: "white",
              padding: "25px",
              borderRadius: "16px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              borderLeft: `5px solid ${stat.color}`,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#64748b",
                    margin: "0 0 8px 0",
                    fontWeight: "600",
                  }}
                >
                  {stat.title}
                </p>
                <h3
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: "800",
                    color: "#1e293b",
                    margin: "0 0 4px 0",
                  }}
                >
                  {stat.value}
                </h3>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: stat.color,
                    background: `${stat.color}15`,
                    padding: "4px 10px",
                    borderRadius: "20px",
                    fontWeight: "600",
                  }}
                >
                  {stat.trend}
                </span>
              </div>
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "12px",
                  background: `${stat.color}20`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem",
                  color: stat.color,
                }}
              >
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "16px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          marginBottom: "25px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              position: "relative",
              flex: "1",
              minWidth: "300px",
            }}
          >
            <FiSearch
              style={{
                position: "absolute",
                left: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                fontSize: "1.1rem",
              }}
            />
            <input
              type="text"
              placeholder="Search enquiries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 20px 12px 45px",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                fontSize: "1rem",
                background: "white",
              }}
            />
          </div>

          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "0.95rem",
              background: "white",
              cursor: "pointer",
              minWidth: "180px",
            }}
          >
            <option value="all">All Projects</option>
            {projects.map((project) => (
              <option key={project.pid} value={project.pid}>
                {project.project_name}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "0.95rem",
              background: "white",
              cursor: "pointer",
              minWidth: "150px",
            }}
          >
            <option value="all">All Leads</option>
            <option value="hot">Hot Leads</option>
            <option value="warm">Warm Leads</option>
            <option value="cold">Cold Leads</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm("");
              setFilterProject("all");
              setFilterStatus("all");
              setSelectedRows([]);
            }}
            style={{
              padding: "12px 20px",
              background: "#f1f5f9",
              border: "none",
              borderRadius: "12px",
              fontSize: "0.95rem",
              cursor: "pointer",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiRefreshCw /> Reset
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Table Header */}
        <div
          style={{
            padding: "20px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3
            style={{
              margin: "0",
              fontSize: "1.2rem",
              fontWeight: "600",
              color: "#1e293b",
            }}
          >
            Enquiries ({filteredEnquiries.length})
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <span style={{ fontSize: "0.9rem", color: "#64748b" }}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "0.9rem",
                background: "white",
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f1f5f9",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            ></div>
            <p style={{ color: "#64748b" }}>Loading enquiries...</p>
          </div>
        ) : sortedEnquiries.length === 0 ? (
          <div
            style={{ textAlign: "center", padding: "60px", color: "#64748b" }}
          >
            <FiMessageSquare
              style={{
                fontSize: "4rem",
                color: "#cbd5e1",
                marginBottom: "20px",
              }}
            />
            <h3
              style={{
                fontSize: "1.5rem",
                color: "#475569",
                marginBottom: "10px",
              }}
            >
              No enquiries found
            </h3>
            <p>Try adjusting your search or add a new enquiry</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: "1200px",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "40px", padding: "16px 20px" }}>
                      <input
                        type="checkbox"
                        checked={
                          paginatedEnquiries.length > 0 &&
                          selectedRows.length === paginatedEnquiries.length
                        }
                        onChange={handleSelectAll}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                    </th>
                    {[
                      { key: "id", label: "ID", width: "80px" },
                      { key: "name", label: "Customer", width: "200px" },
                      { key: "email", label: "Contact", width: "250px" },
                      { key: "project_id", label: "Project", width: "200px" },
                      { key: "note", label: "Notes", width: "300px" },
                      {
                        key: "preferred_time",
                        label: "Preferred Time",
                        width: "150px",
                      },
                      {
                        key: "ready_to_buy_timeline",
                        label: "Timeline",
                        width: "150px",
                      },
                      {
                        key: "site_visit_required",
                        label: "Site Visit",
                        width: "120px",
                      },
                      { key: "created_at", label: "Date", width: "180px" },
                      { key: "actions", label: "Actions", width: "150px" },
                    ].map((column) => (
                      <th
                        key={column.key}
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#475569",
                          borderBottom: "2px solid #e2e8f0",
                          fontSize: "0.9rem",
                          cursor:
                            column.key !== "actions" ? "pointer" : "default",
                          width: column.width,
                        }}
                        onClick={() =>
                          column.key !== "actions" && handleSort(column.key)
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {column.label}
                          {sortConfig.key === column.key &&
                            column.key !== "actions" &&
                            (sortConfig.direction === "asc" ? (
                              <FiArrowUp />
                            ) : (
                              <FiArrowDown />
                            ))}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedEnquiries.map((enquiry) => {
                    const status = getStatusBadge(enquiry);
                    const siteVisit = getSiteVisitBadge(
                      enquiry.site_visit_required,
                    );

                    return (
                      <tr
                        key={enquiry.id}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background 0.2s",
                        }}
                      >
                        <td style={{ padding: "16px 20px" }}>
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(enquiry.id)}
                            onChange={() => handleRowSelect(enquiry.id)}
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                            }}
                          />
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          <span
                            style={{
                              fontWeight: "600",
                              color: "#1e293b",
                            }}
                          >
                            #{enquiry.id}
                          </span>
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg, #667eea, #764ba2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "600",
                                fontSize: "0.9rem",
                              }}
                            >
                              {enquiry.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontWeight: "600",
                                  color: "#1e293b",
                                  marginBottom: "4px",
                                }}
                              >
                                {enquiry.name}
                              </div>
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  padding: "4px 8px",
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  fontWeight: "600",
                                  background: status.bgColor,
                                  color: status.color,
                                }}
                              >
                                {status.text}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          <div style={{ marginBottom: "8px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontSize: "0.9rem",
                              }}
                            >
                              <FiMail
                                style={{ color: "#3b82f6", fontSize: "0.9rem" }}
                              />
                              <a
                                href={`mailto:${enquiry.email}`}
                                style={{
                                  color: "#3b82f6",
                                  textDecoration: "none",
                                }}
                              >
                                {enquiry.email}
                              </a>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "0.9rem",
                            }}
                          >
                            <FiPhone
                              style={{ color: "#10b981", fontSize: "0.9rem" }}
                            />
                            <span style={{ color: "#475569" }}>
                              {enquiry.phone}
                            </span>
                          </div>
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <FiHome
                              style={{ color: "#f59e0b", fontSize: "1rem" }}
                            />
                            <span style={{ fontWeight: "500" }}>
                              {getProjectName(enquiry.project_id)}
                            </span>
                          </div>
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          <div
                            style={{
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: enquiry.note ? "#1e293b" : "#94a3b8",
                              fontStyle: enquiry.note ? "normal" : "italic",
                            }}
                          >
                            {enquiry.note || "No notes provided"}
                          </div>
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          {enquiry.preferred_time ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "#3b82f6",
                              }}
                            >
                              <FiClock />
                              {enquiry.preferred_time}
                            </div>
                          ) : (
                            <span
                              style={{ color: "#94a3b8", fontStyle: "italic" }}
                            >
                              Not specified
                            </span>
                          )}
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          {enquiry.ready_to_buy_timeline ? (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                background: `${getTimelineColor(enquiry.ready_to_buy_timeline)}20`,
                                color: getTimelineColor(
                                  enquiry.ready_to_buy_timeline,
                                ),
                              }}
                            >
                              <MdTimeline />
                              {enquiry.ready_to_buy_timeline}
                            </div>
                          ) : (
                            <span
                              style={{ color: "#94a3b8", fontStyle: "italic" }}
                            >
                              Not specified
                            </span>
                          )}
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "6px 12px",
                              borderRadius: "20px",
                              fontSize: "0.85rem",
                              fontWeight: "600",
                              background: siteVisit.bgColor,
                              color: siteVisit.color,
                            }}
                          >
                            {siteVisit.icon}
                            {siteVisit.text}
                          </div>
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          <div style={{ fontSize: "0.9rem" }}>
                            {formatDate(enquiry.created_at)}
                          </div>
                          <div
                            style={{
                              fontSize: "0.8rem",
                              color: "#94a3b8",
                            }}
                          >
                            {new Date(enquiry.created_at).toLocaleTimeString(
                              [],
                              { hour: "2-digit", minute: "2-digit" },
                            )}
                          </div>
                        </td>

                        <td style={{ padding: "16px 20px" }}>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                            }}
                          >
                            <button
                              onClick={() => handleView(enquiry)}
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "8px",
                                background: "#3b82f6",
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="View Details"
                            >
                              <FiEye />
                            </button>
                            <button
                              onClick={() => handleEdit(enquiry)}
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "8px",
                                background: "#f1f5f9",
                                color: "#475569",
                                border: "1px solid #e2e8f0",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Edit"
                            >
                              <FiEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(enquiry.id)}
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "8px",
                                background: "#fee2e2",
                                color: "#ef4444",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  padding: "20px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "15px",
                }}
              >
                <div style={{ fontSize: "0.9rem", color: "#64748b" }}>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, sortedEnquiries.length)}{" "}
                  of {sortedEnquiries.length} entries
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #e2e8f0",
                      background: "white",
                      borderRadius: "8px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      color: currentPage === 1 ? "#cbd5e1" : "#475569",
                    }}
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        style={{
                          padding: "8px 16px",
                          border: "1px solid #e2e8f0",
                          background:
                            currentPage === pageNum ? "#667eea" : "white",
                          borderRadius: "8px",
                          cursor: "pointer",
                          color: currentPage === pageNum ? "white" : "#475569",
                          fontWeight:
                            currentPage === pageNum ? "600" : "normal",
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #e2e8f0",
                      background: "white",
                      borderRadius: "8px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      color: currentPage === totalPages ? "#cbd5e1" : "#475569",
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "1.5rem", fontWeight: "600" }}>
                {editingEnquiry ? "Edit Enquiry" : "Add New Enquiry"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: "30px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "20px",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                    }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                    }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                    }}
                  >
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                    }}
                  >
                    Project *
                  </label>
                  <select
                    name="project_id"
                    value={formData.project_id}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.pid} value={project.pid}>
                        {project.project_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                  }}
                >
                  Notes
                </label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows="3"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    resize: "vertical",
                  }}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "20px",
                  marginBottom: "25px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                    }}
                  >
                    Preferred Time
                  </label>
                  <select
                    name="preferred_time"
                    value={formData.preferred_time}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  >
                    <option value="">Select Time</option>
                    {timeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                    }}
                  >
                    Timeline
                  </label>
                  <select
                    name="ready_to_buy_timeline"
                    value={formData.ready_to_buy_timeline}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  >
                    <option value="">Select Timeline</option>
                    {timelineOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                    }}
                  >
                    Site Visit
                  </label>
                  <select
                    name="site_visit_required"
                    value={formData.site_visit_required}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  >
                    <option value="">Select Option</option>
                    {siteVisitOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  style={{
                    padding: "12px 25px",
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  style={{
                    padding: "12px 30px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: processing ? "not-allowed" : "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                  }}
                >
                  {processing
                    ? "Processing..."
                    : editingEnquiry
                      ? "Update"
                      : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingEnquiry && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "1.5rem", fontWeight: "600" }}>
                Enquiry Details
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "30px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2rem",
                    color: "white",
                    fontWeight: "600",
                  }}
                >
                  {viewingEnquiry.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "700",
                      margin: "0 0 8px 0",
                    }}
                  >
                    {viewingEnquiry.name}
                  </h3>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      background: getStatusBadge(viewingEnquiry).bgColor,
                      color: getStatusBadge(viewingEnquiry).color,
                      fontSize: "0.85rem",
                      fontWeight: "600",
                    }}
                  >
                    {getStatusBadge(viewingEnquiry).text}
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "25px",
                }}
              >
                <h4
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "15px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Contact Information
                </h4>

                <div style={{ display: "grid", gap: "15px" }}>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <FiMail style={{ color: "#3b82f6" }} />
                      <span style={{ fontSize: "0.9rem", color: "#64748b" }}>
                        Email
                      </span>
                    </div>
                    <a
                      href={`mailto:${viewingEnquiry.email}`}
                      style={{
                        color: "#3b82f6",
                        textDecoration: "none",
                        fontWeight: "500",
                      }}
                    >
                      {viewingEnquiry.email}
                    </a>
                  </div>

                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <FiPhone style={{ color: "#10b981" }} />
                      <span style={{ fontSize: "0.9rem", color: "#64748b" }}>
                        Phone
                      </span>
                    </div>
                    <div style={{ fontWeight: "500" }}>
                      {viewingEnquiry.phone}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <FiHome style={{ color: "#f59e0b" }} />
                      <span style={{ fontSize: "0.9rem", color: "#64748b" }}>
                        Project
                      </span>
                    </div>
                    <div style={{ fontWeight: "500" }}>
                      {getProjectName(viewingEnquiry.project_id)}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "25px",
                }}
              >
                <h4
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "15px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Additional Information
                </h4>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "20px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "#64748b",
                        marginBottom: "4px",
                      }}
                    >
                      Preferred Time
                    </div>
                    <div
                      style={{
                        fontWeight: "500",
                        color: viewingEnquiry.preferred_time
                          ? "#1e293b"
                          : "#94a3b8",
                      }}
                    >
                      {viewingEnquiry.preferred_time || "Not specified"}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "#64748b",
                        marginBottom: "4px",
                      }}
                    >
                      Timeline
                    </div>
                    <div
                      style={{
                        fontWeight: "500",
                        color: viewingEnquiry.ready_to_buy_timeline
                          ? getTimelineColor(
                              viewingEnquiry.ready_to_buy_timeline,
                            )
                          : "#94a3b8",
                      }}
                    >
                      {viewingEnquiry.ready_to_buy_timeline || "Not specified"}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "#64748b",
                        marginBottom: "4px",
                      }}
                    >
                      Site Visit
                    </div>
                    <div
                      style={{
                        fontWeight: "500",
                        color: viewingEnquiry.site_visit_required
                          ? viewingEnquiry.site_visit_required === "Yes"
                            ? "#059669"
                            : "#dc2626"
                          : "#94a3b8",
                      }}
                    >
                      {viewingEnquiry.site_visit_required || "Not specified"}
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "#64748b",
                        marginBottom: "4px",
                      }}
                    >
                      Date
                    </div>
                    <div style={{ fontWeight: "500" }}>
                      {formatDateTime(viewingEnquiry.created_at)}
                    </div>
                  </div>
                </div>
              </div>

              {viewingEnquiry.note && (
                <div
                  style={{
                    background: "#f0f9ff",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "25px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      color: "#0369a1",
                      marginBottom: "15px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Notes
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      lineHeight: 1.6,
                      color: "#1e293b",
                    }}
                  >
                    {viewingEnquiry.note}
                  </p>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingEnquiry);
                  }}
                  style={{
                    padding: "12px 24px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                  }}
                >
                  Edit Enquiry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default EnquiriesManagement;
